/**
 * Recover the real text of a name read out of a GPX document parsed by `gpxparser`.
 *
 * That library reads every text element with `elem.innerHTML`, which is a
 * *serialisation* of the node, not its text. So whatever the author had to
 * escape or wrap comes back still escaped or still wrapped:
 *
 *   <name><![CDATA[Wood Link & Kilmurry]]></name>  ->  "<![CDATA[Wood Link & Kilmurry]]>"
 *   <name>Tom &amp; Jerry</name>                   ->  "Tom &amp; Jerry"
 *
 * AllTrails wraps its names in CDATA, which is how a recording came to be
 * imported and listed as "<![CDATA[Afternoon hike at Warrenscourt…]]>".
 *
 * The correct inverse of a serialisation is to parse it again, so that is what
 * this does, rather than hand-rolling an entity table that would be wrong for
 * numeric references. Stripping the CDATA delimiters textually is NOT a valid
 * alternative: a CDATA section exists precisely to hold characters that would
 * otherwise need escaping, so removing the wrapper around `Wood Link & Kilmurry`
 * yields a bare `&` and malformed XML.
 *
 * Anything that is not a string becomes undefined, and anything that does not
 * round-trip is returned untouched — a name is better shown imperfectly than lost.
 */

/** Only a CDATA wrapper or an entity can need decoding; everything else is already text. */
const NEEDS_DECODE = /<!\[CDATA\[|&/;

export function decodeGpxName(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (value === '' || !NEEDS_DECODE.test(value)) return value;

  try {
    // The input came from innerHTML, so it is a well-formed XML fragment and
    // wrapping it in one element makes a parseable document.
    const doc = new DOMParser().parseFromString(`<hiiker-gpx-text>${value}</hiiker-gpx-text>`, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length > 0) return value;

    const text = doc.documentElement?.textContent;
    return typeof text === 'string' ? text : value;
  } catch {
    return value;
  }
}
